/**
 * reportChartTheme — ONE adapter that turns a report `theme.chartPalette`
 * into the concrete series colours every report chart consumes.
 *
 * Before this, charts mixed three different sources of truth:
 *   - LcoeChartSection had its own `['#0071df', …]` fallback,
 *   - ClimateProfileChart had a different `['#0071df', '#EF9F27', …]`,
 *   - RiskScatterChart hard-coded `#E24B4A / #EF9F27 / #1D9E75`,
 *   - FeasibilityProfileSection's radar read the APP-level `var(--chart-1)`
 *     (cyan dark-theme token) instead of the report theme at all.
 *
 * Routing all of them through `resolveReportChartPalette` means a curated
 * preset (journal / institutional / monograph) — or a tenant's custom
 * palette — reskins every chart with no per-component edit, and the
 * LEGACY (no-preset) path keeps the SAME effective colours it has today.
 *
 * Pure / side-effect-free so it is safe in render and unit-testable.
 */
import type { ReportTheme } from '../types/reportTheme';

/**
 * Stable default series palette. This is the DEFAULT_THEME.chartPalette
 * value — kept duplicated here (not imported) so a chart that somehow
 * receives a malformed/short palette still degrades to the historical
 * colours rather than throwing. Index order: primary, warning, success,
 * danger, accent.
 */
export const DEFAULT_CHART_PALETTE: readonly string[] = [
  '#0071df',
  '#ffab00',
  '#1D9E75',
  '#A32D2D',
  '#7F77DD',
];

/**
 * Resolve the ordered chart-series palette for a theme.
 *
 * Returns AT LEAST `minLength` colours: if the theme palette is missing or
 * shorter than `minLength`, the tail is back-filled from
 * {@link DEFAULT_CHART_PALETTE} (then cycled) so callers can index 0..n-1
 * safely. When the theme carries a full palette this is just that palette.
 *
 * Legacy (no-preset) reports carry `DEFAULT_THEME.chartPalette`, so they
 * resolve to {@link DEFAULT_CHART_PALETTE} unchanged — byte-stable.
 */
export function resolveReportChartPalette(
  theme: Pick<ReportTheme, 'chartPalette'> | null | undefined,
  minLength = 5,
): string[] {
  const fromTheme = theme?.chartPalette;
  const base =
    Array.isArray(fromTheme) && fromTheme.length > 0
      ? fromTheme.filter((c): c is string => typeof c === 'string' && c.length > 0)
      : [];
  if (base.length >= minLength) return [...base];
  // Back-fill / cycle from the stable default so the result is never short.
  const out = [...base];
  let i = 0;
  while (out.length < minLength) {
    out.push(DEFAULT_CHART_PALETTE[i % DEFAULT_CHART_PALETTE.length]!);
    i += 1;
  }
  return out;
}

/**
 * Single series colour at `index`, cycling through the resolved palette so
 * an out-of-range index never returns `undefined`. Used by charts that
 * need one colour (radar primary series, single-series bars).
 */
export function reportChartColor(
  theme: Pick<ReportTheme, 'chartPalette'> | null | undefined,
  index = 0,
): string {
  const palette = resolveReportChartPalette(theme, index + 1);
  return palette[index % palette.length]!;
}
